import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { COMMITTEE, SERVICES } from "@/data/static";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import AnimatedCounter from "@/components/AnimatedCounter";
import KeralaMap from "@/components/KeralaMap";
import DigitalIDCard from "@/components/DigitalIDCard";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InsuranceSection from "@/components/insurance/InsuranceSection";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import Typewriter from "@/components/Typewriter";
import FileViewer from "@/components/FileViewer";
import FeedbackSection from "@/components/FeedbackSection";
import { formatDateDMY } from "@/lib/dates";

export default function Landing() {
  const [stats, setStats] = useState({ total_members: 900, district_units: 14, local_bodies: 900, years_of_service: 14, active_members: 600 });
  const [circulars, setCirculars] = useState([]);
  const [events, setEvents] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [verifyId, setVerifyId] = useState("");
  const [regState, setRegState] = useState({ open: true, closed_message: "" });
  const location = useLocation();

  // hash deep-link from the navbar (e.g. /#group-insurance)
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.hash]);

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/circulars").then(r => setCirculars(r.data)).catch(() => {});
    api.get("/events").then(r => setEvents(r.data)).catch(() => {});
    api.get("/districts").then(r => setDistricts(r.data)).catch(() => {});
    api.get("/gallery").then(r => setGallery(r.data)).catch(() => {});
    api.get("/settings/membership").then(r => setRegState(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <AnnouncementBanner />
      <Navbar />
      <main className="flex-1">
        <Hero verifyId={verifyId} setVerifyId={setVerifyId} regState={regState} />
        <InsuranceSection />
        <StatsBar stats={stats} />
        <About />
        <Services />
        <section id="districts" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <KeralaMap districtData={districts} />
        </section>
        <Committee />
        <CircularsAndEvents circulars={circulars} events={events} />
        <GalleryPreview items={gallery} />
        <FeedbackSection />
        <DigitalCardSection />
      </main>
      <Footer />
    </div>
  );
}

/* ---------- Sections ---------- */

function Hero({ verifyId, setVerifyId, regState = { open: true, closed_message: "" } }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern" />
      <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-brand-blue/10 blur-3xl" />
      <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-brand-orange/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="outline" className="border-brand-blue/30 text-brand-blue bg-white/60 dark:bg-card mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green animate-pulse mr-2" />
              Official Association Portal — Kerala
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight min-h-[3.6em]">
              <Typewriter
                speed={42}
                startDelay={250}
                segments={[
                  { text: "The technical backbone of ", className: "" },
                  { text: "Digital Kerala's ", className: "text-brand-blue" },
                  { text: "local bodies", className: "text-brand-orange" },
                  { text: ".", className: "" },
                ]}
              />
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              KELTA unites Technical Assistants across Grama Panchayats, Block Panchayats, District
              Panchayats, Municipalities and Corporations — driving e-governance, IT infrastructure
              and digital public services in every corner of Kerala.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {regState.open ? (
                <Link to="/register">
                  <Button size="lg" className="bg-brand-blue hover:bg-brand-blue/90 text-white px-7 h-12" data-testid="hero-join-btn">
                    Join Membership <Icons.ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <div className="flex flex-col gap-1">
                  <Button
                    size="lg"
                    disabled
                    className="bg-muted text-muted-foreground px-7 h-12 cursor-not-allowed"
                    data-testid="hero-join-btn-closed"
                    title={regState.closed_message}
                  >
                    Registration Closed <Icons.Lock className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs text-muted-foreground max-w-[280px]" data-testid="hero-closed-message">
                    {regState.closed_message}
                  </p>
                </div>
              )}
              <Link to="/login">
                <Button size="lg" variant="outline" className="h-12 px-7" data-testid="hero-login-btn">
                  Member Login
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              <div className="absolute -inset-8 id-holo opacity-20 blur-3xl rounded-full" />
              <DigitalIDCard />
              <div className="absolute -bottom-4 -left-4 bg-card rounded-xl border border-border p-3 shadow-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-green/15 flex items-center justify-center">
                  <Icons.QrCode className="w-5 h-5 text-brand-green" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">QR Verified</div>
                  <div className="text-sm font-semibold">Live verification</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function StatsBar({ stats }) {
  const items = [
    { label: "Total Members", value: stats.total_members, suffix: "+", icon: "Users" },
    { label: "District Units", value: stats.district_units, suffix: "", icon: "MapPin" },
    { label: "Local Bodies", value: stats.local_bodies, suffix: "+", icon: "Building2" },
    { label: "Years of Service", value: stats.years_of_service, suffix: "", icon: "Calendar" },
    { label: "Active Members", value: stats.active_members, suffix: "+", icon: "Activity" },
  ];
  return (
    <section className="py-12 bg-brand-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-6">
        {items.map((it, i) => {
          const Icon = Icons[it.icon] || Icons.Activity;
          return (
            <motion.div
              key={it.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="text-center md:text-left"
              data-testid={`stat-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon className="w-5 h-5 text-brand-orange mb-2 mx-auto md:mx-0" />
              <div className="font-black text-3xl sm:text-4xl tracking-tight">
                <AnimatedCounter value={it.value} suffix={it.suffix} />
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{it.label}</div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Badge variant="outline" className="mb-4">About KELTA</Badge>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            A statewide network of technical talent serving public governance.
          </h2>
        </div>
        <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
          <BentoCard
            title="Vision"
            body="To be the trusted technical force enabling transparent, efficient and citizen-centric local self-government across Kerala."
            color="blue"
            icon="Eye"
          />
          <BentoCard
            title="Mission"
            body="Empower every panchayat, municipality and corporation with skilled technical assistants, modern systems and shared knowledge."
            color="green"
            icon="Target"
          />
          <BentoCard
            title="Objectives"
            body="Welfare, training, technical advisory, knowledge exchange, and unified representation of technical assistants."
            color="orange"
            icon="Compass"
          />
        </div>
      </div>
    </section>
  );
}

function BentoCard({ title, body, color, icon }) {
  const Icon = Icons[icon] || Icons.Square;
  const colors = {
    blue: "bg-brand-blue/10 text-brand-blue",
    green: "bg-brand-green/10 text-brand-green",
    orange: "bg-brand-orange/10 text-brand-orange",
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-6 hover-lift">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center mb-4 ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-bold text-xl mb-2">{title}</div>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}

function Services() {
  return (
    <section id="services" className="py-24 bg-muted/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <Badge variant="outline" className="mb-3">Services</Badge>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight max-w-3xl">
              What technical assistants do across <span className="text-brand-blue">Kerala's local bodies</span>.
            </h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Cog;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group rounded-2xl border border-border bg-card p-6 hover-lift hover:border-brand-blue/40"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">0{i + 1}</span>
                </div>
                <div className="font-bold text-lg mb-1">{s.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Committee() {
  return (
    <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <Badge variant="outline" className="mb-3">Executive Committee</Badge>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tight">Leading the association</h2>
        <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
          The state committee elected by members to represent KELTA.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {COMMITTEE.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="rounded-2xl border border-border bg-card overflow-hidden hover-lift"
          >
            <div className="aspect-[4/3] overflow-hidden bg-muted">
              <img src={p.img} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="text-xs uppercase tracking-wider text-brand-orange font-semibold">{p.role}</div>
              <div className="font-bold text-lg mt-1">{p.name}</div>
              <a href={`tel:${p.phone}`} className="text-sm text-muted-foreground hover:text-brand-blue inline-flex items-center gap-1.5 mt-1">
                <Icons.Phone className="w-3.5 h-3.5" /> {p.phone}
              </a>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CircularsAndEvents({ circulars, events }) {
  const [active, setActive] = React.useState(null);
  return (
    <section className="py-24 bg-muted/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-6 flex items-center justify-between border-b border-border">
            <div>
              <div className="text-xs uppercase tracking-wider text-brand-blue font-semibold">Latest</div>
              <h3 className="text-2xl font-black tracking-tight">Circulars & Orders</h3>
            </div>
            <Icons.FileText className="w-6 h-6 text-muted-foreground" />
          </div>
          <ul className="divide-y divide-border">
            {circulars
              .slice()
              .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
              .slice(0, 5)
              .map((c) => (
              <li key={c.id} data-testid={`circular-${c.id}`}>
                <button
                  type="button"
                  onClick={() => setActive(c)}
                  className="w-full text-left p-5 hover:bg-muted/40 transition-colors group"
                  data-testid={`circular-open-${c.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="text-[10px]">{c.category}</Badge>
                    <span className="text-xs text-muted-foreground">{formatDateDMY(c.date)}</span>
                    {c.file_url && (
                      <span className="ml-auto text-[10px] uppercase tracking-wider text-brand-blue font-semibold inline-flex items-center gap-1">
                        <Icons.Eye className="w-3 h-3" /> View
                      </span>
                    )}
                  </div>
                  <div className="font-semibold group-hover:text-brand-blue transition-colors">{c.title}</div>
                  <p className="text-sm text-muted-foreground line-clamp-1">{c.summary}</p>
                </button>
              </li>
            ))}
            {circulars.length === 0 && (
              <li className="p-8 text-sm text-muted-foreground text-center">No circulars yet.</li>
            )}
          </ul>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-6 flex items-center justify-between border-b border-border">
            <div>
              <div className="text-xs uppercase tracking-wider text-brand-green font-semibold">Upcoming</div>
              <h3 className="text-2xl font-black tracking-tight">Events & Training</h3>
            </div>
            <Icons.Calendar className="w-6 h-6 text-muted-foreground" />
          </div>
          <ul className="divide-y divide-border">
            {events.slice(0, 5).map((e) => (
              <li key={e.id} className="p-5 flex gap-4" data-testid={`event-${e.id}`}>
                <div className="flex-shrink-0 w-14 text-center">
                  <div className="text-[10px] uppercase text-brand-orange font-bold">{new Date(e.date).toLocaleString("en", { month: "short" })}</div>
                  <div className="text-2xl font-black">{new Date(e.date).getDate()}</div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px]">{e.type}</Badge>
                  </div>
                  <div className="font-semibold truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Icons.MapPin className="w-3 h-3" /> {e.location}
                  </div>
                </div>
              </li>
            ))}
            {events.length === 0 && (
              <li className="p-8 text-sm text-muted-foreground text-center">No upcoming events.</li>
            )}
          </ul>
        </div>
      </div>
      <FileViewer
        open={!!active}
        onClose={() => setActive(null)}
        title={active?.title}
        url={active?.file_url}
      />
    </section>
  );
}


function GalleryPreview({ items }) {
  if (!items || items.length === 0) return null;
  const preview = items.slice(0, 6);
  return (
    <section className="py-24 bg-muted/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <Badge variant="outline" className="mb-3">Gallery</Badge>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight max-w-3xl">
              Moments documenting <span className="text-brand-orange">KELTA's work</span>.
            </h2>
            <p className="text-muted-foreground mt-2 max-w-xl text-sm">
              Meetings, recognitions, training programs and milestones across Kerala.
            </p>
          </div>
          <Link to="/gallery">
            <Button variant="outline">View full gallery →</Button>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {preview.map((g, i) => (
            <motion.div
              key={g.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className={`relative rounded-xl overflow-hidden border border-border bg-card group hover-lift ${i === 0 ? "row-span-2 md:row-span-2" : ""}`}
            >
              <Link to="/gallery">
                <img
                  src={g.image_url}
                  alt={g.caption || "KELTA"}
                  loading="lazy"
                  className={`w-full ${i === 0 ? "h-full min-h-[320px]" : "h-48 md:h-52"} object-cover group-hover:scale-[1.03] transition-transform duration-500`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  {g.category && <Badge className="text-[10px] bg-brand-orange/95 border-none">{g.category}</Badge>}
                  {g.caption && <p className="text-xs text-white mt-1.5 line-clamp-2">{g.caption}</p>}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DigitalCardSection() {
  return (
    <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Badge variant="outline" className="mb-3">Digital Membership Card</Badge>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            One card. Anywhere. <span className="text-brand-blue">Instantly verifiable.</span>
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed max-w-lg">
            Every approved KELTA member receives a unique digital ID card with a QR code linked
            to a public verification page. Carry it in your phone, print it for in-office use.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "Unique ID format: KELTA-2026-00001",
              "QR scans to instant verification page",
              "Photo, designation, district & expiry",
              "Auto-renewed on yearly payment",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <Icons.Check className="w-4 h-4 text-brand-green" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3">
            <Link to="/register">
              <Button className="bg-brand-blue hover:bg-brand-blue/90 text-white">Get your ID card</Button>
            </Link>
          </div>
        </div>
        <div className="flex justify-center">
          <DigitalIDCard />
        </div>
      </div>
    </section>
  );
}
